import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PersonIcon from "@mui/icons-material/Person";
import { Box, InputAdornment, TextField } from "@mui/material";
import { useField } from "formik";

import { useAppDialog } from "hooks/useAppDialog";
import { useGetOrganizationUsers } from "hooks/useGetOrganizationUsers";
import { useSelectedOrganization } from "hooks/useSelectedOrganization";
import { useUsersDataGrid } from "hooks/useUsersDataGrid";
import { User } from "types/User";

interface AddUserProps {
  name: string;
  label: string;
}

export const AddUser = ({ name, label }: AddUserProps): JSX.Element => {
  const [candidate, setCandidate] = useState<User>();
  const [usersLoading, setUsersLoading] = useState(false);
  const { t } = useTranslation();
  const [{ value }, { error, touched }, { setValue, setTouched }] =
    useField(name);
  const hasError = useMemo(() => touched && Boolean(error), [touched, error]);
  const { openDialog, Dialog, dialogProps, closeDialog } = useAppDialog();

  const onClick = useCallback(() => {
    setTouched(true);
    openDialog();
  }, [setTouched, openDialog]);

  const onSubmit = useCallback(() => {
    if (!candidate) {
      return;
    }
    setValue(candidate);
    closeDialog();
  }, [candidate, setValue, closeDialog]);

  return (
    <>
      <TextField
        label={label}
        onClick={onClick}
        fullWidth
        value={
          value
            ? `${value.firstName} ${value.lastName} ${
                value.workEmail ? `(${value.workEmail})` : ""
              }`
            : ""
        }
        error={hasError}
        helperText={hasError ? error : " "}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <PersonIcon />
            </InputAdornment>
          ),
        }}
      />
      <Dialog
        {...dialogProps}
        title={t("AddUser")}
        loading={usersLoading}
        onSubmit={onSubmit}
        actionText={t("Save")}
        maxWidth="lg"
      >
        <SelectUserDataGrid
          onSelect={setCandidate}
          onLoadingChange={setUsersLoading}
        />
      </Dialog>
    </>
  );
};

interface SelectUserDataGridProps {
  onSelect: (user: User) => void;
  onLoadingChange: (isLoading: boolean) => void;
}

const SelectUserDataGrid = ({
  onSelect,
  onLoadingChange,
}: SelectUserDataGridProps): JSX.Element => {
  const { page, pageSize, search, UsersDataGrid, usersDataGridProps } =
    useUsersDataGrid();
  const { selectedOrganizationId } = useSelectedOrganization();
  const { isLoading, usersResponse } = useGetOrganizationUsers({
    organizationId: selectedOrganizationId as string,
    params: {
      s: search,
      startRecord: page * pageSize,
      pageSize,
      outputOrganizations: false,
      outputGroups: false,
      outputDefaultOrg: false,
    },
  });

  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  return (
    <Box sx={{ height: 500, flex: 1, display: "flex" }}>
      <UsersDataGrid
        isLoading={isLoading}
        data={usersResponse}
        hideEdit
        {...usersDataGridProps}
        disableSelectionOnClick={false}
        setUser={(selectedId) => {
          const selected = usersResponse?.matches?.find(
            (item) => item.id === selectedId[0]
          );
          if (selected) {
            onSelect(selected);
          }
        }}
        singleSelection={true}
      />
    </Box>
  );
};
